import type { ITrustCenterStandardName } from '@/lib/constants/standards'

export const PUBLIC_TRUST_CENTER = {
  TITLE: 'Trust Center',
  PAGE_TITLE: (organization: string) => `${organization} Trust Center`,
  NAV: {
    CERTIFICATIONS: 'Certifications',
    CONTROLS: 'Controls',
    DOCUMENTS: 'Documents',
    SUBPROCESSORS: 'Subprocessors',
    REQUEST_ACCESS: 'Request access',
    POWERED_BY: 'Powered by',
  },
  FOOTER: {
    COPYRIGHT: (year: number, organization: string) => `© ${year} ${organization}. All rights reserved.`,
  },
  OVERVIEW: {
    LAST_UPDATED: (date: string) => `Last updated ${date}`,
    MONITORING: (controls: number, domains: number) => `Monitoring ${controls} controls across ${domains} domains`,
    STATS: {
      CERTIFICATIONS: 'Certifications',
      CONTROLS: 'Controls monitored',
      DOCUMENTS: 'Documents',
      SUBPROCESSORS: 'Subprocessors',
    },
    CERTIFICATIONS: {
      TITLE: 'Certifications and frameworks',
      VIEW_DETAILS: 'View details',
    },
    CONTROLS: {
      TITLE: 'Security controls',
      SUBTITLE: (count: number) => `${count} controls monitored continuously`,
      VIEW_ALL: 'View all controls',
    },
    DOCUMENTS: {
      TITLE: 'Documents',
      SUBTITLE: 'Confidential documents released under NDA',
      VIEW_ALL: 'View all documents',
    },
    SUBPROCESSORS: {
      TITLE: 'Subprocessors',
      SUBTITLE: 'Third parties that may process customer data',
      VIEW_ALL: 'View all',
    },
    CONTACTS: {
      VULNERABILITY: {
        TITLE: 'Report a vulnerability',
        BODY: 'Coordinated disclosure with acknowledgement within 48 hours.',
      },
      QUESTIONNAIRES: {
        TITLE: 'Security questionnaires',
        BODY: 'Most questions are answered by the documents above. Send anything still open.',
      },
    },
  },
  CERTIFICATIONS: {
    TITLE: 'Certifications and frameworks',
    CERTIFICATE: 'Certificate',
    EMPTY: 'No certifications yet',
    STATUS: {
      CERTIFIED: 'Certified',
    },
    DESCRIPTIONS: {
      'ISO 27001': 'Information security management',
      DORA: 'Digital operational resilience',
      ENS: 'Esquema Nacional de Seguridad',
      'PCI DSS': 'Payment card data security',
      'ISO 9001': 'Quality management',
      NIS2: 'EU network and information security',
      SOC2: 'Security, availability, confidentiality',
      'ISO 42001': 'AI management system',
      RGPD: 'EU data protection regulation',
      'ISO 14001': 'Environmental management',
      'ISO 27017': 'Cloud services security',
      'ISO 27018': 'Cloud privacy (PII protection)',
    } satisfies Record<ITrustCenterStandardName, string>,
    SUMMARIES: {
      'ISO 27001': 'Risk-based controls that keep information confidential, intact and available',
      DORA: 'Resilience of the ICT systems supporting financial services in the EU',
      ENS: 'Security requirements for systems serving the Spanish public sector',
      'PCI DSS': 'Protection of cardholder data when stored, processed and transmitted',
      'ISO 9001': 'Consistent, customer-focused processes under continual improvement',
      NIS2: 'Cybersecurity risk management and incident reporting for essential services',
      SOC2: 'Independent assurance over security, availability and confidentiality controls',
      'ISO 42001': 'Responsible development and use of artificial intelligence',
      RGPD: 'Lawful and transparent processing of personal data under EU law',
      'ISO 14001': 'Systematic management and reduction of environmental impact',
      'ISO 27017': 'Security controls specific to providing and using cloud services',
      'ISO 27018': 'Protection of personal data processed in public cloud services',
    } satisfies Record<ITrustCenterStandardName, string>,
  },
  CONTROLS: {
    TITLE: 'Security controls',
    SUBTITLE: (controls: number, domains: number) => `${controls} controls across ${domains} domains`,
    COUNT: (count: number) => `${count} controls`,
    SEARCH_PLACEHOLDER: 'Search controls',
    SHOWING_ALL: (total: number) => `Showing all ${total}`,
    SHOWING: (shown: number, total: number) => `Showing ${shown} of ${total}`,
    EMPTY: 'No controls match this search',
    ID: 'ID',
    CONTROL: 'Control',
    DOMAIN: 'Domain',
    REQUEST_EVIDENCE: 'Request evidence',
    EVIDENCE_RESOURCE: (domain: string) => `${domain} evidence`,
    OTHER_DOMAINS: 'Other domains',
  },
  RESOURCES: {
    TITLE: 'Documents',
    SUBTITLE: 'Public documents download directly. Confidential ones are released after an NDA.',
    FILTER_ALL: 'All',
    FILTER_PUBLIC: 'Public',
    FILTER_PRIVATE: 'Under NDA',
    PUBLIC: 'Public',
    PRIVATE: 'Under NDA',
    DOWNLOAD: 'Download',
    REQUEST: 'Request',
    EMPTY: 'No documents',
  },
  ERROR: {
    SOMETHING_WENT_WRONG: 'Oops! Something went wrong',
    TITLE: 'Error loading public resources',
    DESCRIPTION: "We couldn't load the public resources. This might be temporary, please try again.",
    RETRY: 'Try again',
    WRONG_EMAIL: 'Wrong email format',
  },
  SUBPROCESSORS: {
    TITLE: 'Subprocessors and privacy',
    SUBTITLE: 'Third parties that may process customer data, and how that data is handled',
    PROVIDER: 'Provider',
    PURPOSE: 'Purpose',
    LOCATION: 'Location',
    EMPTY: 'No subprocessors',
    PRIVACY: {
      RESIDENCY: {
        TITLE: 'Data residency in the EU',
        BODY: 'Customer data is stored and processed in EU regions. No transfers outside the EEA without SCCs.',
      },
      RETENTION: {
        TITLE: 'Retention and deletion',
        BODY: 'Data is deleted after contract termination, and backups expire in line with our retention policy.',
      },
      RIGHTS: {
        TITLE: 'Data subject rights',
        BODY: 'Access, rectification and erasure requests are answered within 30 days.',
      },
      GDPR: {
        TITLE: 'GDPR',
        BODY: 'DPA available on request, with the current subprocessor list attached.',
      },
    },
  },
  REQUEST_ACCESS: {
    TITLE: 'Request document access',
    DESCRIPTION: 'Confidential documents are shared once your request is reviewed.',
    FULL_NAME: 'Full name',
    FULL_NAME_PLACEHOLDER: 'Jane Doe',
    EMAIL: 'Work email',
    EMAIL_PLACEHOLDER: 'jane@company.com',
    COMPANY: 'Company',
    COMPANY_PLACEHOLDER: 'Company S.L.',
    DOCUMENTS: 'Documents requested',
    REASON: 'Reason for the request',
    REASON_PLACEHOLDER: 'Vendor security review ahead of contract renewal',
    REQUIRED: 'Name, email and a document required',
    READY: 'Answered within one business day',
    CANCEL: 'Cancel',
    SUBMIT: 'Send request',
    SENT_TITLE: 'Request sent',
    SENT_DESCRIPTION: 'The team will review your request and reply by email.',
    CLOSE: 'Close',
  },
  ACCESS_REQUEST_EMAIL: {
    SUBJECT: 'New access request in your Trust Center',
    GREETING: 'Hello,',
    INTRO: 'Someone has requested access to documents in your Trust Center. These are the details:',
    NAME: 'Name',
    EMAIL: 'Email',
    COMPANY: 'Company',
    DOCUMENTS: 'Documents',
    REASON: 'Reason',
    NEXT_STEPS: 'You can reply to them directly at their email.',
    LINKS_NOTE: 'Documents with a link are included above.',
    SIGNATURE: 'Best regards,\nThe Qalea team',
  },
} as const
